import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Loader2 } from "lucide-react";
import useRealtimeOrders from "@/hooks/useRealtimeOrders";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { new: "Nouvelle", accepted: "Acceptée", preparing: "En préparation", ready: "Prête", rejected: "Refusée", delivered: "Livrée" };
const PAY = { cash: "Espèces à la livraison", wave: "Wave", orange_money: "Orange Money", card: "Carte" };
const NEXT = { new: [["accepted", "Accepter"], ["rejected", "Refuser"]], accepted: [["preparing", "Lancer la préparation"]], preparing: [["ready", "Marquer prête"]] };

const OrderCard = ({ o, onSet, busy }) => {
  const ks = o.status === "cancelled" ? "rejected" : o.status === "delivered" ? "delivered" : o.restaurant_status || "new";
  return (
    <div className={`rounded-2xl border p-4 space-y-2 ${ks === "new" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 bg-[#1E293B]"}`} data-testid={`kitchen-order-${o.id}`}>
      <div className="flex justify-between items-center">
        <p className="font-black text-sm">#{o.id.slice(0, 8)} · {o.customer_name || "Client"}</p>
        <span className="text-[10px] font-black uppercase tracking-widest text-[#D4AF37]" data-testid={`kitchen-status-${o.id}`}>{LABELS[ks]}</span>
      </div>
      <ul className="text-xs text-slate-300">
        {(o.food_items || []).map((it) => <li key={it.cart_item_id}>{it.quantity}× {it.name}</li>)}
      </ul>
      <p className="text-xs text-slate-300" data-testid={`kitchen-address-${o.id}`}>📍 {o.delivery_address || "Adresse non précisée"}</p>
      <p className="text-xs text-slate-400" data-testid={`kitchen-amount-${o.id}`}>
        Total {Math.round(o.total_amount || (o.items_amount || 0) + (o.delivery_fee || 0))} FCFA · {PAY[o.payment_method] || o.payment_method || "Espèces"} · net resto {Math.round(o.partner_earning || 0)} FCFA · {new Date(o.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
      </p>
      <div className="flex gap-2 flex-wrap">
        {(NEXT[ks] || []).map(([s, label]) => (
          <button key={s} type="button" disabled={busy} onClick={() => onSet(o.id, s)} className={`rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-widest ${s === "rejected" ? "bg-white/10 text-rose-300" : "bg-[#D4AF37] text-[#0F172A]"}`} data-testid={`kitchen-${s}-${o.id}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Commandes Food reçues en temps réel par le restaurant
export const RestaurantOrdersPanel = ({ user }) => {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/restaurant/orders`).then((r) => setOrders(r.data)).catch(() => setOrders([])), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useRealtimeOrders(user?.id, (event, data) => {
    if (event === "restaurant_new_order") {
      toast.success(`Nouvelle commande #${(data?.order_id || "").slice(0, 8)} !`);
      load();
    }
  });

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      await axios.put(`${API}/restaurant/orders/${id}/status`, { status });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (!orders) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  return (
    <section className="space-y-3" data-testid="restaurant-orders-panel">
      <h2 className="text-sm font-black uppercase tracking-widest flex items-center gap-2"><Bell size={16} className="text-[#D4AF37]" /> Commandes reçues</h2>
      {orders.length === 0 ? <p className="text-xs text-slate-400" data-testid="kitchen-empty">Aucune commande pour le moment.</p>
        : orders.map((o) => <OrderCard key={o.id} o={o} onSet={setStatus} busy={busy} />)}
    </section>
  );
};
